import {
  Button,
  Flex,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import React, {
  forwardRef,
  memo,
  useCallback,
  useState,
  useImperativeHandle,
  useRef,
  useMemo,
} from "react";

interface InputDialogProps {
  title: string;
  description: string;
  initialValue?: string;
  setResult?: (value: string | false) => void;
}

export interface InputDialogRef {
  openDialog: (initialValue?: string) => void;
}

const InputDialog = memo(
  forwardRef<InputDialogRef, InputDialogProps>(
    ({ title, description, setResult, initialValue }, ref) => {
      const { isOpen, onOpen, onClose } = useDisclosure();

      const [inputValue, setInputValue] = useState("");

      const handleOpen = useCallback(
        (initialValue?: string) => {
          setInputValue(initialValue || "");
          onOpen();
        },
        [onOpen],
      );

      const handleCancel = useCallback(() => {
        onClose();
        setResult?.(false);
        setInputValue("");
      }, [onClose, setResult]);

      const handleAccept = useCallback(() => {
        onClose();
        setResult?.(inputValue);
        setInputValue("");
      }, [inputValue, onClose, setResult]);

      const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(event.target.value);
      };

      useImperativeHandle(ref, () => ({
        openDialog: (initialValue) => handleOpen(initialValue),
      }));

      return (
        <Modal
          isOpen={isOpen}
          onClose={handleCancel}
          aria-labelledby="input-dialog-title"
          aria-describedby="input-dialog-description"
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader id="input-dialog-title">{title}</ModalHeader>
            <ModalCloseButton />
            <ModalBody gap={4}>
              <Flex gap={2} direction={"column"}>
                <Text>{description}</Text>
                <Input
                  value={inputValue}
                  onChange={handleChange}
                  placeholder="Enter value"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleAccept();
                    }
                  }}
                  autoFocus
                />
              </Flex>
            </ModalBody>
            <ModalFooter gap={2}>
              <Button
                onClick={handleCancel}
                borderColor="brand.500"
                variant="outline"
                borderRadius={8}
                color="brand.500"
              >
                Cancel
              </Button>
              <Button
                bgColor="brand.500"
                color={"white"}
                borderRadius={8}
                _hover={{ backgroundColor: "brand.600" }}
                onClick={handleAccept}
                autoFocus
              >
                Confirm
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      );
    },
  ),
);

export default InputDialog;

export const useInputDialog = (props: Omit<InputDialogProps, "setResult">) => {
  const ref = useRef<InputDialogRef>(null);
  const [resolveResult, setResolveResult] =
    useState<(value: string | false) => void>();

  const openDialog = useCallback((initialValue?: string) => {
    return new Promise<string | false>((resolve) => {
      setResolveResult(() => resolve);
      ref.current?.openDialog(initialValue);
    });
  }, []);

  const InputDialogComponent = useMemo(
    () => (
      <InputDialog
        {...props}
        ref={ref}
        setResult={(value) => {
          resolveResult?.(value);
        }}
      />
    ),
    [props, resolveResult],
  );

  return {
    InputDialog: InputDialogComponent,
    openDialog,
  };
};
